import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut } from 'lucide-react';
import { resolveImgUrl } from '@/utils/imgUrl';

const HARI  = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', "Jum'at", 'Sabtu'];
const BULAN = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

const ROLE_BADGE = {
  guru:  { label: 'Guru Tahfizh',  color: 'bg-blue-400/20 text-blue-100'   },
  admin: { label: 'Administrator', color: 'bg-amber-400/20 text-amber-100' },
};

function getGreeting(hour) {
  if (hour < 5)  return { sapa: 'Malam Barakah',  pesan: 'Qiyamullail penuh keberkahan.' };
  if (hour < 12) return { sapa: 'Selamat Pagi',   pesan: "Mari raih keberkahan Al-Qur'an hari ini!" };
  if (hour < 15) return { sapa: 'Selamat Siang',  pesan: 'Setiap ayat menjadi pemberat timbangan amal.' };
  if (hour < 18) return { sapa: 'Selamat Sore',   pesan: 'Waktunya murojaah untuk menjaga hafalan.' };
  return          { sapa: 'Selamat Malam',  pesan: 'Semoga esok lebih semangat untuk Tahajud.' };
}

/**
 * SmartHeader — identik dengan header SantriDashboard.
 * Props:
 *   foto       — URL foto profil (opsional)
 *   onLogout   — callback tombol logout
 *   badge      — JSX badge di bawah nama (override; jika null, pakai role badge otomatis)
 *   extra      — JSX ekstra di sisi kanan header (opsional)
 */
export default function SmartHeader({ foto, onLogout, badge, extra }) {
  const { user } = useAuth();
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const hari    = HARI[time.getDay()];
  const tanggal = `${hari}, ${time.getDate()} ${BULAN[time.getMonth()]} ${time.getFullYear()}`;
  const jam     = time.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const greet   = getGreeting(time.getHours());

  // Badge: pakai prop jika diberikan; jika tidak, coba auto-generate dari role
  const resolvedBadge = badge !== undefined ? badge
    : (user?.role && ROLE_BADGE[user.role])
      ? (
        <span className={`inline-block text-[11px] font-bold px-2.5 py-0.5 rounded-full ${ROLE_BADGE[user.role].color}`}>
          {ROLE_BADGE[user.role].label}
        </span>
      ) : null;

  return (
    <div className="bg-gradient-to-br from-emerald-950 via-emerald-800 to-emerald-700 px-5 pt-12 pb-5 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/5 rounded-full pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-amber-400/10 rounded-full pointer-events-none" />

      <div className="relative">
        {/* Photo · Info · Actions */}
        <div className="flex items-start gap-4">

          {/* Profile photo with amber ring */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-full ring-[3px] ring-amber-400 ring-offset-2 ring-offset-emerald-900 overflow-hidden bg-emerald-700 flex items-center justify-center shadow-xl">
              {foto
                ? <img src={resolveImgUrl(foto)} alt={user?.name} className="w-full h-full object-cover" />
                : <span className="text-white font-bold text-xl">{user?.name?.charAt(0)}</span>}
            </div>
          </div>

          {/* Name + badge + message */}
          <div className="flex-1 min-w-0">
            <p className="text-emerald-300 text-[10px] uppercase tracking-widest mb-0.5">{greet.sapa}</p>
            <h1 className="font-playfair text-xl font-bold text-white leading-tight truncate">{user?.name}</h1>
            {resolvedBadge && <div className="mt-1">{resolvedBadge}</div>}
            <p className="text-emerald-400 text-[10px] mt-1.5 italic leading-snug">{greet.pesan}</p>
          </div>

          {/* Logout + extras */}
          <div className="flex items-center gap-2 flex-shrink-0 mt-1">
            {extra}
            {onLogout && (
              <button onClick={onLogout} data-testid="smart-header-logout-btn"
                className="p-2 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-colors">
                <LogOut size={17} />
              </button>
            )}
          </div>
        </div>

        {/* Date + live time */}
        <div className="mt-4 bg-white/10 rounded-2xl px-4 py-2.5 flex items-center justify-between">
          <p className="text-emerald-200 text-xs">{tanggal}</p>
          <p className="text-white font-bold text-sm tabular-nums">{jam}</p>
        </div>
      </div>
    </div>
  );
}
